/**
 * Markdown reporter — generates a Markdown report.
 */

import { writeFile } from 'node:fs/promises';
import type { IReporter } from './interfaces.js';
import type { AnalysisResult } from '../types/analysis.js';
import { ReportFormat } from '../types/common.js';

export class MarkdownReporter implements IReporter {
  readonly format = ReportFormat.Markdown;
  readonly name = 'markdown';

  async generate(result: AnalysisResult): Promise<string> {
    const lines: string[] = [];

    lines.push(`# 🛡️ pkg-sentinel Report`);
    lines.push('');
    lines.push(`**Package:** \`${result.packageName}@${result.packageVersion}\``);
    lines.push(`**Analyzed:** ${new Date(result.analyzedAt).toLocaleString()}`);
    lines.push(`**Duration:** ${result.totalDurationMs}ms`);
    lines.push('');

    // Trust score
    lines.push(`## Trust Score: ${result.trustScore.overall}/100 (${result.trustScore.level})`);
    lines.push('');
    lines.push(`> ${result.trustScore.summary}`);
    lines.push('');

    // Score breakdown
    lines.push('## Score Breakdown');
    lines.push('');
    lines.push('| Category | Score | Weight | Explanation |');
    lines.push('|----------|-------|--------|-------------|');
    for (const cat of result.trustScore.categories) {
      lines.push(`| ${cat.label} | ${cat.score}/100 | ${cat.weight}% | ${cat.explanation} |`);
    }
    lines.push('');

    // Findings
    lines.push(`## Findings (${result.findings.length})`);
    lines.push('');

    if (result.findings.length > 0) {
      lines.push('| Severity | Rule | Title | Confidence |');
      lines.push('|----------|------|-------|------------|');
      for (const finding of result.findings) {
        lines.push(`| ${finding.severity.toUpperCase()} | \`${finding.ruleId}\` | ${finding.title} | ${finding.confidence}% |`);
      }
    } else {
      lines.push('✅ No security findings detected.');
    }

    lines.push('');
    lines.push('---');
    lines.push('*Generated by [pkg-sentinel](https://github.com/pkg-sentinel/pkg-sentinel)*');

    return lines.join('\n');
  }

  async output(result: AnalysisResult, destination?: string): Promise<void> {
    const md = await this.generate(result);
    if (destination) {
      await writeFile(destination, md, 'utf-8');
    } else {
      process.stdout.write(md + '\n');
    }
  }
}
